import { parseApiInstant } from "../datetime/parseApiInstant";

export type DueTimeOfDay = "morning" | "afternoon" | "evening" | "night";

export interface DueTimeOfDayPresentation {
  period: DueTimeOfDay;
  /** e.g. "Thursday Night" */
  headline: string;
  /** e.g. "Jun 14, 2026 at 9:00 PM" */
  detail: string;
}

function hourOfDay(d: Date): number {
  return d.getHours() + d.getMinutes() / 60;
}

export function dueTimeOfDay(iso: string | null | undefined): DueTimeOfDay | null {
  const d = parseApiInstant(iso);
  if (!d) return null;
  const h = hourOfDay(d);
  if (h >= 5 && h < 12) return "morning";
  if (h >= 12 && h < 17) return "afternoon";
  if (h >= 17 && h < 21) return "evening";
  return "night";
}

const PERIOD_LABEL: Record<DueTimeOfDay, string> = {
  morning: "Morning",
  afternoon: "Afternoon",
  evening: "Evening",
  night: "Night",
};

export function formatDuePresentation(iso: string | null | undefined): DueTimeOfDayPresentation | null {
  const d = parseApiInstant(iso);
  if (!d) return null;
  const period = dueTimeOfDay(iso)!;
  const weekday = d.toLocaleDateString(undefined, { weekday: "long" });
  const headline = `${weekday} ${PERIOD_LABEL[period]}`;
  const detail = d.toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
  return { period, headline, detail };
}

export function formatDueDateCompactLines(
  iso: string | null | undefined
): { line1: string; line2: string } | null {
  const d = parseApiInstant(iso);
  if (!d) return null;
  return {
    line1: d.toLocaleDateString(undefined, { month: "long", day: "numeric" }),
    line2: d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }),
  };
}

export function formatClosedDateCompactLines(
  iso: string | null | undefined
): { line1: string; line2: string } | null {
  const d = parseApiInstant(iso);
  if (!d) return null;
  const dateLine = d.toLocaleDateString(undefined, { month: "long", day: "numeric" });
  return {
    line1: `Closed ${dateLine}`,
    line2: d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }),
  };
}

export function formatDueDateButtonLabel(iso: string | null | undefined): string {
  if (!iso) return "No due date";
  const lines = formatDueDateCompactLines(iso);
  if (!lines) return "No due date";
  return `${lines.line1}\n${lines.line2}`;
}

export function formatClosedDateLabel(iso: string | null | undefined): string {
  const lines = formatClosedDateCompactLines(iso);
  if (!lines) return "Closed";
  return `${lines.line1}\n${lines.line2}`;
}
